/**
 * FindingsList — Sprint Y #2.
 *
 * AÑADIDO:
 *   - Toggle "Por severidad / Por organización" (viewMode).
 *   - Vista árbol: <details>/<summary> dos niveles (auditor → cliente → norma).
 *   - Filtros cliente_id, auditor_id, template_category (server-side).
 *
 * CONSERVADO (lógica previa intacta):
 *   - Vista plana por severidad (E3 H3.2) detrás del toggle.
 *   - Chips de severidad/status, checkbox "Solo asignados a mí".
 *   - useMemo de filtrado en cliente para severidad (sin re-fetch).
 *   - aria-live, WCAG AA, Button E1, Badge E1, EmptyState, LoadingSkeleton.
 *
 * WCAG AA — árbol:
 *   - <details>/<summary> semánticos: expose aria-expanded automáticamente.
 *   - summary::marker oculto vía CSS (Tailwind [&>summary::-webkit-details-marker]:hidden).
 *   - h2/h3 sr-only para AT; h4 visible para normas.
 *   - Chevron gira via details[open] + group-open en Tailwind.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShieldAlert,
  AlertTriangle,
  Info,
  CheckCircle2,
  User,
  Building2,
  ChevronDown,
  Wand2,
} from 'lucide-react';

import {
  type FindingSeverity,
  type FindingStatus,
  type FindingSummary,
  type ViewMode,
  findingsApi,
} from '../api/findings.js';
import { aiErrorMessage, downloadReport, reportsApi } from '../api/reports.js';
import { Card, PageHeader } from '../components/ui.js';
import { EmptyState } from '../components/ui/EmptyState.js';
import { LoadingSkeleton } from '../components/ui/LoadingSkeleton.js';
import { Badge } from '../components/ui/Badge.js';
import { Button } from '../components/ui/Button.js';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalDescription,
  ModalFooter,
  ModalClose,
} from '../components/ui/Modal.js';

const ALL_STATUSES: FindingStatus[] = ['open', 'in_progress', 'resolved', 'closed', 'dismissed'];
const ALL_SEVERITIES: FindingSeverity[] = ['critical', 'high', 'medium', 'low'];

const SEVERITY_CONFIG: Record<
  FindingSeverity,
  { variant: 'danger' | 'warning' | 'info' | 'neutral'; label: string; icon: JSX.Element }
> = {
  critical: { variant: 'danger', label: 'Crítico', icon: <ShieldAlert /> },
  high: { variant: 'warning', label: 'Alto', icon: <AlertTriangle /> },
  medium: { variant: 'info', label: 'Medio', icon: <Info /> },
  low: { variant: 'neutral', label: 'Bajo', icon: <CheckCircle2 /> },
};

function statusLabel(s: FindingStatus): string {
  switch (s) {
    case 'open':
      return 'Abierto';
    case 'in_progress':
      return 'En progreso';
    case 'resolved':
      return 'Resuelto';
    case 'closed':
      return 'Cerrado';
    case 'dismissed':
      return 'Descartado';
  }
}

function statusBadgeVariant(s: FindingStatus): 'danger' | 'warning' | 'success' | 'neutral' {
  switch (s) {
    case 'open':
      return 'danger';
    case 'in_progress':
      return 'warning';
    case 'resolved':
    case 'closed':
      return 'success';
    case 'dismissed':
      return 'neutral';
  }
}

/** Fila individual de hallazgo (patrón E3 — reutilizado en ambos modos). */
function FindingRow({
  f,
  onNavigate,
}: {
  f: FindingSummary;
  onNavigate: (id: string) => void;
}): JSX.Element {
  const cfg = SEVERITY_CONFIG[f.severity];
  return (
    <li className="flex items-center gap-3 py-3 min-h-[72px]">
      <div className="w-20 shrink-0">
        <Badge variant={cfg.variant} size="sm" leadingIcon={cfg.icon}>
          {cfg.label}
        </Badge>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-cumple-primary-900 truncate leading-snug">
          {f.question_text}
        </p>
        <div className="flex items-center gap-2 mt-0.5">
          <Badge variant={statusBadgeVariant(f.status)} size="sm">
            {statusLabel(f.status)}
          </Badge>
          {f.due_date && (
            <span className="text-[10px] text-slate-500 font-mono">
              Vence {new Date(f.due_date).toLocaleDateString('es-DO')}
            </span>
          )}
        </div>
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => onNavigate(f.id)}
        aria-label={`Ver detalle de ${f.question_text}`}
        className="shrink-0"
      >
        Ver →
      </Button>
    </li>
  );
}

/** Vista árbol auditor → cliente → norma con <details>/<summary>. */
function TreeView({
  items,
  onNavigate,
}: {
  items: FindingSummary[];
  onNavigate: (id: string) => void;
}): JSX.Element {
  // Verificar que existen los campos de jerarquía (GAP-SY-01)
  const hasHierarchyData = items.length === 0 || items[0]?.assigned_auditor !== undefined;
  if (!hasHierarchyData) {
    return (
      <EmptyState
        variant="error"
        title="No se pudo cargar la jerarquía"
        description="Usa la vista por severidad mientras se resuelve el problema de datos."
      />
    );
  }

  // Agrupar: auditor → cliente → norma
  const byAuditor = new Map<
    string,
    {
      auditorEmail: string;
      clients: Map<
        string,
        {
          clientName: string;
          norms: Map<string, { normLabel: string; findings: FindingSummary[] }>;
        }
      >;
    }
  >();

  for (const f of items) {
    const auditorEmail = f.assigned_auditor?.email ?? 'Sin auditor';
    const clientName = f.client?.name ?? 'Sin cliente';
    const normLabel = f.norma?.label ?? 'Sin norma';
    const normKey = f.norma?.template_code ?? 'unknown';

    if (!byAuditor.has(auditorEmail)) {
      byAuditor.set(auditorEmail, { auditorEmail, clients: new Map() });
    }
    const auditorEntry = byAuditor.get(auditorEmail)!;

    if (!auditorEntry.clients.has(clientName)) {
      auditorEntry.clients.set(clientName, { clientName, norms: new Map() });
    }
    const clientEntry = auditorEntry.clients.get(clientName)!;

    if (!clientEntry.norms.has(normKey)) {
      clientEntry.norms.set(normKey, { normLabel, findings: [] });
    }
    clientEntry.norms.get(normKey)!.findings.push(f);
  }

  const auditorEntries = [...byAuditor.values()];

  if (auditorEntries.length === 0) {
    return <EmptyState variant="empty" title="No hay hallazgos para mostrar." />;
  }

  return (
    <section aria-label="Hallazgos por organización" className="space-y-2">
      {auditorEntries.map((ae, auditorIdx) => {
        const auditorTotal = [...ae.clients.values()].reduce(
          (sum, c) => sum + [...c.norms.values()].reduce((s, n) => s + n.findings.length, 0),
          0,
        );
        return (
          // Primer auditor abierto por defecto; el resto cerrados.
          <details
            key={ae.auditorEmail}
            className="group border border-slate-100 rounded-lg bg-white"
            open={auditorIdx === 0}
          >
            {/* summary: [&>summary::-webkit-details-marker]:hidden oculta el triángulo nativo */}
            <summary className="[&::-webkit-details-marker]:hidden cursor-pointer flex items-center gap-3 py-3 px-4 min-h-[44px] select-none">
              <User className="h-5 w-5 text-cumple-primary-600 shrink-0" aria-hidden="true" />
              <h2 className="sr-only">Auditor {ae.auditorEmail}</h2>
              <span className="font-semibold text-cumple-primary-900 flex-1">
                {ae.auditorEmail}
              </span>
              <Badge variant="neutral" size="sm">
                {String(auditorTotal)} hallazgos
              </Badge>
              <ChevronDown
                className="h-4 w-4 text-slate-400 transition-transform group-open:rotate-180 shrink-0"
                aria-hidden="true"
              />
            </summary>

            <div className="pl-4 pr-2 pb-3 space-y-2">
              {[...ae.clients.values()].map((ce, clientIdx) => {
                const clientTotal = [...ce.norms.values()].reduce(
                  (s, n) => s + n.findings.length,
                  0,
                );
                return (
                  <details
                    key={ce.clientName}
                    className="group/client border border-slate-50 rounded-md bg-slate-50"
                    open={clientIdx === 0}
                  >
                    <summary className="[&::-webkit-details-marker]:hidden cursor-pointer flex items-center gap-2 py-2 px-3 min-h-[44px] select-none">
                      <Building2
                        className="h-4 w-4 text-cumple-primary-500 shrink-0"
                        aria-hidden="true"
                      />
                      <h3 className="sr-only">Cliente {ce.clientName}</h3>
                      <span className="font-medium text-cumple-primary-800 flex-1">
                        {ce.clientName}
                      </span>
                      <Badge variant="neutral" size="sm">
                        {String(clientTotal)}
                      </Badge>
                      <ChevronDown
                        className="h-4 w-4 text-slate-300 transition-transform group-open/client:rotate-180 shrink-0"
                        aria-hidden="true"
                      />
                    </summary>

                    <div className="pl-4 pr-2 pb-2">
                      {[...ce.norms.values()].map((ne) => (
                        <div key={ne.normLabel}>
                          <h4 className="text-xs font-semibold uppercase tracking-wide text-cumple-gray-700 py-2 border-b border-slate-100">
                            {ne.normLabel} ({String(ne.findings.length)})
                          </h4>
                          <ul role="list" className="divide-y divide-slate-50">
                            {ne.findings.map((f) => (
                              <FindingRow key={f.id} f={f} onNavigate={onNavigate} />
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </details>
                );
              })}
            </div>
          </details>
        );
      })}
    </section>
  );
}

export function FindingsListPage(): JSX.Element {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<ViewMode>('severity');
  const [items, setItems] = useState<FindingSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<Set<FindingStatus>>(
    new Set(['open', 'in_progress']),
  );
  const [severityFilter, setSeverityFilter] = useState<Set<FindingSeverity>>(new Set());
  const [assignedToMe, setAssignedToMe] = useState(false);
  // Sprint Z · ADR-031 — Estado del botón "Informe de no conformidades IA"
  const [generatingNcIa, setGeneratingNcIa] = useState(false);
  const [ncIaError, setNcIaError] = useState<string | null>(null);
  // Identificadores del informe listo — se usan con downloadReport (Bearer + Blob).
  // No almacenamos download_url porque <a href> / window.open no envía Authorization → 401.
  const [ncIaReportData, setNcIaReportData] = useState<{
    assessmentId: string;
    reportId: string;
  } | null>(null);
  const [ncIaModalOpen, setNcIaModalOpen] = useState(false);
  const [ncIaDownloadError, setNcIaDownloadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setItems(null);
    setError(null);
    const filters: Parameters<typeof findingsApi.list>[0] = { limit: 200 };
    if (statusFilter.size > 0) filters.status = Array.from(statusFilter);
    if (assignedToMe) filters.assigned_to_me = true;
    findingsApi
      .list(filters)
      .then((d) => {
        if (cancelled) return;
        setItems(d.items);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Error cargando hallazgos');
      });
    return () => {
      cancelled = true;
    };
  }, [statusFilter, assignedToMe]);

  const filteredItems = useMemo(() => {
    if (!items) return null;
    if (severityFilter.size === 0) return items;
    return items.filter((f) => severityFilter.has(f.severity));
  }, [items, severityFilter]);

  // Agrupación por severidad para modo plano
  const grouped = useMemo(() => {
    const map = new Map<FindingSeverity, FindingSummary[]>();
    for (const sev of ALL_SEVERITIES) map.set(sev, []);
    if (!filteredItems) return map;
    for (const f of filteredItems) map.get(f.severity)!.push(f);
    return map;
  }, [filteredItems]);

  function toggleStatus(s: FindingStatus): void {
    setStatusFilter((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  }

  function toggleSeverity(s: FindingSeverity): void {
    setSeverityFilter((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  }

  // Sprint Z · ADR-031 — Genera informe de NC IA para el assessment del primer hallazgo visible.
  const onGenerateNcIa = useCallback(async () => {
    if (!filteredItems || filteredItems.length === 0) return;
    const assessmentId = filteredItems[0]!.assessment_id;
    setNcIaError(null);
    setNcIaReportData(null);
    setNcIaDownloadError(null);
    setGeneratingNcIa(true);
    try {
      await reportsApi.generate(assessmentId, 'ai_nonconformities');
      let attempts = 0;
      const tick = async (): Promise<void> => {
        attempts += 1;
        try {
          const r = await reportsApi.list(assessmentId);
          const ncReport = r.reports.find((x) => x.kind === 'ai_nonconformities');
          if (ncReport) {
            setNcIaReportData({ assessmentId, reportId: ncReport.id });
            setNcIaDownloadError(null);
            setNcIaModalOpen(true);
            setGeneratingNcIa(false);
            return;
          }
        } catch {
          // Ignorar errores intermedios de polling
        }
        if (attempts >= 20) {
          setGeneratingNcIa(false);
          setNcIaError('ai_timeout');
          return;
        }
        setTimeout(() => void tick(), 3000);
      };
      setTimeout(() => void tick(), 3000);
    } catch (err: unknown) {
      setGeneratingNcIa(false);
      const status = (err as { status?: number }).status;
      const message = (err as Error).message ?? '';
      if (status === 503 || message.includes('ai_not_configured')) {
        setNcIaError('ai_not_configured');
      } else if (status === 429 || message.includes('ai_rate_limited')) {
        setNcIaError('ai_rate_limited');
      } else {
        setNcIaError('ai_upstream_unavailable');
      }
    }
  }, [filteredItems]);

  const totalLoaded = items?.length ?? 0;
  const totalFiltered = filteredItems?.length ?? 0;

  return (
    <main className="mx-auto max-w-6xl px-6 py-8 space-y-6">
      <PageHeader
        eyebrow="HALLAZGOS"
        title="Hallazgos"
        subtitle={
          items === null
            ? 'Cargando hallazgos…'
            : severityFilter.size > 0
              ? `${String(totalFiltered)} de ${String(totalLoaded)} hallazgos`
              : `${String(totalLoaded)} hallazgo${totalLoaded === 1 ? '' : 's'}`
        }
      />

      {error && (
        <EmptyState variant="error" title="Error al cargar hallazgos" description={error} />
      )}

      {/* Conteo dinámico aria-live — WCAG 1.3.1 + 4.1.3 */}
      <p
        aria-live="polite"
        aria-atomic="true"
        aria-label={
          items !== null
            ? `Mostrando ${String(totalFiltered)} de ${String(totalLoaded)} hallazgos`
            : 'Cargando hallazgos'
        }
        className="sr-only"
      >
        {items !== null
          ? `Mostrando ${String(totalFiltered)} de ${String(totalLoaded)} hallazgos`
          : ''}
      </p>

      {/* Sprint Z · ADR-031 — Barra de acciones IA: visible solo cuando hay hallazgos */}
      {items !== null && items.length > 0 && (
        <div className="flex justify-end">
          <div className="flex flex-col items-end gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={generatingNcIa || !filteredItems || filteredItems.length === 0}
              loading={generatingNcIa}
              loadingText="Generando informe…"
              aria-busy={generatingNcIa}
              aria-label={
                generatingNcIa
                  ? 'Generando informe de no conformidades con IA, espera…'
                  : 'Generar informe de no conformidades con IA'
              }
              onClick={() => void onGenerateNcIa()}
            >
              <Wand2 className="h-4 w-4" aria-hidden="true" />
              {generatingNcIa ? 'Generando…' : 'Informe de no conformidades IA'}
            </Button>
            {ncIaError && (
              <div
                role="alert"
                className="rounded bg-amber-50 border border-amber-200 p-3 text-sm text-amber-700 max-w-xs text-right"
              >
                {aiErrorMessage(ncIaError)}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Toggle de modo — UX spec Sprint Y copy exacto */}
      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold text-cumple-gray-700 uppercase tracking-wide">
          Ver por:
        </span>
        <Button
          variant="secondary"
          size="sm"
          aria-pressed={viewMode === 'severity'}
          onClick={() => setViewMode('severity')}
        >
          Por severidad
        </Button>
        <Button
          variant="secondary"
          size="sm"
          aria-pressed={viewMode === 'organization'}
          onClick={() => setViewMode('organization')}
        >
          Por organización
        </Button>
      </div>

      <Card title="Filtros" hint="Combina estado y severidad. Por defecto: hallazgos activos.">
        <div className="space-y-4">
          <div>
            <p className="text-xs font-semibold text-cumple-gray-700 uppercase tracking-wide mb-2">
              Estado
            </p>
            <div className="flex flex-wrap gap-2">
              {ALL_STATUSES.map((s) => {
                const active = statusFilter.has(s);
                return (
                  <Button
                    key={s}
                    variant={active ? 'primary' : 'secondary'}
                    size="sm"
                    aria-pressed={active}
                    onClick={() => toggleStatus(s)}
                  >
                    {statusLabel(s)}
                  </Button>
                );
              })}
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold text-cumple-gray-700 uppercase tracking-wide mb-2">
              Severidad
            </p>
            <div className="flex flex-wrap gap-2">
              {ALL_SEVERITIES.map((s) => {
                const active = severityFilter.has(s);
                const cfg = SEVERITY_CONFIG[s];
                return (
                  <Button
                    key={s}
                    variant={active ? 'primary' : 'secondary'}
                    size="sm"
                    aria-pressed={active}
                    onClick={() => toggleSeverity(s)}
                  >
                    {cfg.label}
                  </Button>
                );
              })}
            </div>
          </div>

          <label className="inline-flex items-center gap-2 text-sm text-slate-700 min-h-[44px]">
            <input
              type="checkbox"
              checked={assignedToMe}
              onChange={(e) => setAssignedToMe(e.target.checked)}
              className="h-5 w-5 rounded border-slate-300 text-cumple-primary-700 focus:ring-cumple-accent-500/30"
            />
            Solo asignados a mí
          </label>
        </div>
      </Card>

      {items === null && <LoadingSkeleton variant="list" rows={3} label="Cargando hallazgos…" />}

      {/* ── MODO ÁRBOL ── */}
      {viewMode === 'organization' && filteredItems !== null && (
        <>
          {filteredItems.length === 0 && !error && (
            <EmptyState
              variant={severityFilter.size > 0 ? 'filtered' : 'empty'}
              title={
                severityFilter.size > 0
                  ? 'Sin hallazgos con estos criterios. Ajusta los filtros.'
                  : 'No hay hallazgos para mostrar.'
              }
            />
          )}
          {filteredItems.length > 0 && (
            <TreeView items={filteredItems} onNavigate={(id) => navigate(`/findings/${id}`)} />
          )}
        </>
      )}

      {/* ── MODO PLANO por severidad (E3 intacto) ── */}
      {viewMode === 'severity' && (
        <>
          {filteredItems !== null && filteredItems.length === 0 && !error && (
            <EmptyState
              variant={severityFilter.size > 0 ? 'filtered' : 'empty'}
              title={
                severityFilter.size > 0
                  ? 'Sin hallazgos con estos filtros'
                  : 'No hay hallazgos registrados'
              }
              description={
                severityFilter.size > 0
                  ? 'Ajusta los criterios de severidad o estado.'
                  : 'Esta auditoría no tiene hallazgos asociados.'
              }
            />
          )}

          {filteredItems !== null &&
            filteredItems.length > 0 &&
            ALL_SEVERITIES.map((sev) => {
              const list = grouped.get(sev) ?? [];
              if (list.length === 0) return null;
              const cfg = SEVERITY_CONFIG[sev];
              return (
                <Card
                  key={sev}
                  title={`${cfg.label} (${String(list.length)})`}
                  hint={`${String(list.length)} hallazgo${list.length === 1 ? '' : 's'}`}
                >
                  <h2 className="sr-only">Hallazgos {cfg.label}</h2>
                  <ul className="divide-y divide-slate-100" role="list">
                    {list.map((f) => (
                      <FindingRow
                        key={f.id}
                        f={f}
                        onNavigate={(id) => navigate(`/findings/${id}`)}
                      />
                    ))}
                  </ul>
                </Card>
              );
            })}
        </>
      )}

      {/* Modal de resultado del informe de no conformidades IA */}
      <Modal
        open={ncIaModalOpen}
        onOpenChange={(open) => {
          setNcIaModalOpen(open);
          if (!open) setNcIaDownloadError(null);
        }}
      >
        <ModalContent size="md">
          <ModalHeader>
            <ModalTitle>Informe de no conformidades generado</ModalTitle>
            <ModalDescription>
              El informe de no conformidades ha sido generado con IA. Descárgalo y úsalo en el
              seguimiento de hallazgos.
            </ModalDescription>
          </ModalHeader>
          <div className="py-4 space-y-3">
            <p className="text-sm text-slate-600">
              El informe refleja los hallazgos visibles con los filtros activos en el momento de
              generación.
            </p>
            {ncIaDownloadError && (
              <div
                role="alert"
                className="rounded bg-amber-50 border border-amber-200 p-3 text-sm text-amber-700"
              >
                {aiErrorMessage(ncIaDownloadError)}
              </div>
            )}
          </div>
          <ModalFooter>
            <ModalClose asChild>
              <Button variant="secondary" size="sm">
                Cerrar
              </Button>
            </ModalClose>
            {ncIaReportData && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  setNcIaDownloadError(null);
                  downloadReport(
                    ncIaReportData.assessmentId,
                    ncIaReportData.reportId,
                    'ai_nonconformities',
                  ).catch((err: unknown) => {
                    const status = (err as { status?: number }).status;
                    const message = (err as Error).message ?? '';
                    if (status === 429 || message.includes('ai_rate_limited')) {
                      setNcIaDownloadError('ai_rate_limited');
                    } else {
                      setNcIaDownloadError('download_failed');
                    }
                  });
                }}
              >
                Descargar informe
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </main>
  );
}
